import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../services/query/queryKeys';
import { appwriteQueryFns } from '../services/query/appwriteQueryFns';
import { useAuth } from '../contexts/AuthContext';
import { useRegistrationStatus } from './useRegistrationStatus';

export const useClientProfile = (userEmailOverride?: string) => {
  const { currentUser, targetEmail } = useAuth();
  const { isRegistered } = useRegistrationStatus();
  const userEmail = userEmailOverride || targetEmail || currentUser?.email;
  const isImpersonating = Boolean(targetEmail && targetEmail !== currentUser?.email);
  const prevEmailRef = useRef<string | null | undefined>(userEmail);
  const queryClient = useQueryClient();

  // The email from the previous render cycle — captured before we overwrite it below.
  const previousEmail = prevEmailRef.current;

  // Detect when the query email changes (impersonation start/stop).
  const emailChanged = previousEmail !== null && previousEmail !== userEmail;

  // Update the ref for the next cycle immediately (before effects fire).
  prevEmailRef.current = userEmail;

  useEffect(() => {
    if (emailChanged) {
      // Remove the OLD profile query from cache so TanStack Query can't serve
      // the impersonated user's stale credits when the admin's own key mounts.
      queryClient.removeQueries({
        queryKey: queryKeys.profile.client(previousEmail),
        exact: true,
      });

      // Force a refetch of the NEW profile query. refetchOnMount doesn't fire
      // when the query key changes within the same component — it only fires
      // on initial component mount. Without this, the admin sees their own
      // stale cached credits (from before impersonation started).
      queryClient.refetchQueries({
        queryKey: queryKeys.profile.client(userEmail),
        exact: true,
      });
    }
  }, [emailChanged, previousEmail, userEmail, queryClient]);

  return useQuery({
    queryKey: queryKeys.profile.client(userEmail || ''),
    queryFn: () => appwriteQueryFns.profile.client(userEmail!),
    // Allow impersonation to fetch the target profile even if the local registration gate is false.
    enabled: !!userEmail && (isRegistered || isImpersonating),
    staleTime: 5 * 60 * 1000,        // 5 minutes — credits are shared but don't change mid-session
                                     // often enough to warrant sub-minute polling.
    gcTime: 60 * 1000,               // 1 minute — keep in cache briefly for navigation feel
    refetchOnWindowFocus: false,     // Drop tab-focus refetches — was the #1 source of n8n-proxy
                                     // executions (get_credit_balance called on every tab switch).
    refetchOnReconnect: true,
    // Credits MUST be accurate — refetch on mount only when stale (5 min) to avoid unnecessary
    // get_credit_balance calls during idle navigation.
    refetchOnMount: true,
    // When crossing an impersonation boundary, do NOT carry over the previous
    // query's data — that would briefly show the wrong user's credits.
    placeholderData: emailChanged
      ? undefined
      : (previousData: any) => previousData,
  });
};